import { useState, type FormEvent } from 'react';
import { useIssueLink, useIssueOptions } from '@/api/hooks';
import { useAgent } from '@/auth/use-agent';
import type { IssuedLink } from '@/api/types';
import { formatDateTime } from '@/lib/format';
import { isPlayerId, PLAYER_ID_EXAMPLE } from '@/lib/player-id';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Combobox } from '@/ui/Combobox';
import { ErrorNotice } from '@/ui/ErrorNotice';
import { Field, INPUT_CLASS } from '@/ui/Field';
import { Loading } from '@/ui/Loading';

function IssuedCard({ issued, onReset }: { issued: IssuedLink; onReset(): void }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(issued.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Card title="Link ready">
      <p className="break-all rounded-md bg-slate-50 p-3 font-mono text-xs">{issued.url}</p>
      <p className="mt-2 text-sm text-slate-500">Valid until {formatDateTime(issued.expiresAt)}</p>
      <div className="mt-4 flex gap-2">
        <Button type="button" onClick={copy}>
          {copied ? 'Copied' : 'Copy link'}
        </Button>
        <Button type="button" variant="secondary" onClick={onReset}>
          New link
        </Button>
      </div>
    </Card>
  );
}

export function LinkBuilderPage() {
  const { agent } = useAgent();
  const options = useIssueOptions();
  const issue = useIssueLink();
  const [tournamentId, setTournamentId] = useState('');
  const [locale, setLocale] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState('');
  const [formKey, setFormKey] = useState(0);
  const [validation, setValidation] = useState<string | null>(null);

  const tournaments = options.data?.tournaments ?? [];
  const selectedLocale = locale ?? options.data?.defaultLocale ?? '';

  function reset() {
    issue.reset();
    setPlayerId('');
    setValidation(null);
    setFormKey((key) => key + 1);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!tournamentId) {
      setValidation('Choose a tournament from the list.');
      return;
    }
    if (!isPlayerId(playerId)) {
      setValidation(`The player id must start with PLAYER-, for example ${PLAYER_ID_EXAMPLE}.`);
      return;
    }
    setValidation(null);
    issue.mutate({ tournamentId, playerUuid: playerId.trim(), locale: selectedLocale });
  }

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Link builder</h1>
        <p className="mt-1 text-sm text-slate-500">
          Generates a magic link for one player. Only that player can use it, and it works until the tournament ends.
          Links you generate are issued as <span className="font-semibold text-slate-700">{agent.agentId}</span>.
        </p>
      </div>

      {issue.data ? (
        <IssuedCard issued={issue.data} onReset={reset} />
      ) : (
        <Card>
          {options.isPending && <Loading what="tournaments" />}
          <ErrorNotice error={options.error} />
          {options.data && (
            <form onSubmit={submit} className="space-y-4" autoComplete="off" key={formKey}>
              <Field id="tournament" label="Tournament">
                <Combobox
                  id="tournament"
                  items={tournaments.map((tournament) => ({
                    value: tournament.id,
                    label: tournament.title || tournament.id,
                    meta: `${tournament.title ? `${tournament.id} · ` : ''}ends ${formatDateTime(tournament.endAt)}`,
                  }))}
                  value={tournamentId}
                  onChange={setTournamentId}
                  placeholder={tournaments.length ? 'Search by name or id' : 'No running tournament'}
                  disabled={!tournaments.length}
                  emptyText="No tournament matches"
                />
              </Field>
              <Field id="locale" label="Language">
                <select
                  id="locale"
                  className={INPUT_CLASS}
                  value={selectedLocale}
                  onChange={(event) => setLocale(event.target.value)}
                >
                  {options.data.locales.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                id="player"
                label="Player ID"
                hint="Copy it from the player's profile in the backoffice, including the PLAYER- prefix. A wrong id produces a link nobody can use."
              >
                <input
                  id="player"
                  type="text"
                  required
                  className={INPUT_CLASS}
                  placeholder={PLAYER_ID_EXAMPLE}
                  value={playerId}
                  onChange={(event) => setPlayerId(event.target.value)}
                />
              </Field>
              {validation && <ErrorNotice error={new Error(validation)} />}
              <ErrorNotice error={issue.error} />
              <Button type="submit" disabled={issue.isPending || !tournaments.length} className="w-full">
                {issue.isPending ? 'Generating…' : 'Generate link'}
              </Button>
            </form>
          )}
        </Card>
      )}
    </div>
  );
}
